/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import {mat4, vec3, vec2} from "gl-matrix";
import {RenderDocument, Utils, Debug} from "js_renderer";
import IHWCameraModule from "./scene_hw_camera_interface";
import Scene from "../../scene";

import {importInstascan} from '../../dynamic_import';

/**
 * Instascan library is loaded dynamically!
 *
 * @param {cadwork.webgl.SceneAPI.Scene} aSceneAPI
 * @constructor
 */
export default
class HWCameraModule extends AbstractModule implements IHWCameraModule {

    protected static _typeName: string = "hwcamera";

    private _video: any;
    private _enabled: boolean;
    private _libraryLoaded: boolean;        // true on ready
    private _loadingLibrary: boolean;       // true if loading

    private _devices: any[];
    private _activeDeviceIndex: number;

    private _scanner: any;
    private _lastScan: any;
    private _ready: boolean;
    private _listener: any;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);
        this._type = HWCameraModule._typeName;

        /** @type HTMLVideoElement */
        this._video = null;
        this._enabled = false;

        this._libraryLoaded = false;        // true on ready
        this._loadingLibrary = false;       // true if loading

        // Get supported devices from WebRTC
        this._devices = [];
        this._activeDeviceIndex = -1;

        // Create QR Scanner
        /** @type Instascan.Scanner */
        this._scanner = null;
        this._lastScan = null;
    }

    public static getClassType(): string {
        return HWCameraModule._typeName;
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize) {
        //
    }

    /**
     * @return {boolean}
     */
    public isSupported(): boolean {
        return !!(navigator && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices);
    }

    /**
     * @return {boolean}
     */
    public isEnabled(): boolean {
        return this._enabled;
    }

    /**
     * @return {boolean}
     */
    public isReady(): boolean {
        return this._ready;
    }

    /**
     * Clear last scan.
     */
    public clear() {
        this._lastScan = null;
    }

    /**
     * Prepare this module, load library. When ready, call callback function of a listener.
     *
     * @return {boolean} true if already init (and callback will NOT be called)
     */
    public async init() {
        if (this._libraryLoaded) {
            return true;
        }

        if (this._loadingLibrary) {
            return false;
        }

        this._loadingLibrary = true;

        let onSuccess = () => {
            this._libraryLoaded = true;
            this._loadingLibrary = false;

            if (this._listener) {
                this._listener.onHWCameraModuleInit();
            }
        };

        importInstascan();

        return true;
    }

    /**
     * Set video element, where camera will be rendered
     *
     * @param {HTMLVideoElement} videoElement
     */
    public setVideoElement(videoElement: HTMLVideoElement) {
        // Release old if any
        if (this._video) {
            this._video.pause();
            this._video.srcObject = null;
        }

        this._video = videoElement;

        // Add events
        this._video.autoplay = true;
        this._video.playsinline = true;
        Utils.addEvent(this._video, 'canplay', () => {
            this._ready = true;
        });
        Utils.addEvent(this._video, 'emptied', () => {
            this._ready = false;
        });
    }

    /**
     * @return {HTMLVideoElement}
     */
    public getVideoElement(): HTMLVideoElement {
        // Internal video element
        if (this._video === null) {
            this._video = document.createElement('video');
            this._video.autoplay = true;
            this._video.playsinline = true;

            Utils.addEvent(this._video, 'canplay', () => {
                this._ready = true;
            });

            Utils.addEvent(this._video, 'emptied', () => {
                this._ready = false;
            });
        }

        return this._video;
    }

    /**
     * Async! When done, cbObject[cbFunction](devices) is called.
     *
     * @param {object} cbObject
     * @param {string} cbFunction
     */
    public rescanVideoDevices(cbObject, cbFunction) {
        // @ts-ignore
        if (!Instascan) {
            Debug.logError("HWCameraModule: Instascan not loaded");
            return;
        }

        // @ts-ignore
        Instascan.Camera.getCameras().then((cameras) => {
            this._devices = cameras;
            cbObject[cbFunction](this._devices);

        }).catch(function(e) {
            Debug.logError("HWCameraModule: Cannot get cameras! " + e);
        });
    }

    /**
     * @return {Array}
     */
    public getDevices() {
        return this._devices;
    }

    /**
     * @return {object | null}
     */
    public getActiveDevice() {
        if (this._activeDeviceIndex < 0 || !this._enabled) {
            return null;
        }
        return this._devices[this._activeDeviceIndex];
    }

    /**
     * Scan now. Null on error. Use listener to get 'onScanChange' event.
     *
     * @return {object | null}
     */
    public scan() {
        if (this._scanner === null) {
            Debug.logWarning("HWCameraModule: Scanner is not active");
            return null;
        }

        let scan = this._scanner.scan();
        let same = true;

        if (this._lastScan === null && scan !== null) {
            same = false;
        } else if (scan !== null && this._lastScan.content !== scan.content) {
            same = false;
        }

        if (!same) {
            this._lastScan = scan;
            if (this._listener) {
                this._listener.onScanChanged(scan);
            }
        }

        return this._lastScan;
    }

    /**
     * @param {{content: string}} scan
     */
    public parseScan(scan) {
        let str = scan.content;
        let lines = str.split("\n");
        let result = {};

        for (let line of lines) {
            let data = line.split(":");
            let name = data[0].trim();
            result[name] = data[1] === undefined ? "null" : data[1].trim();
        }

        return result;
    }

    /**
     * This will create scanner. Or if exists, destroy the old one and asynchronously create new.
     *
     * @param {number} index
     * @private
     */
    public _createScanner(index) {
        // @ts-ignore
        if (!Instascan) {
            Debug.logError("HWCameraModule: Instascan not loaded");
            return;
        }

        // Stop if exists
        if (this._scanner) {
            this._scanner.stop().then(() => {
                this._scanner = null;
                this._createScanner(index);

            }).catch(function(e) {
                Debug.logError("HWCameraModule: cannot stop scanner! " + e);
            });

            return;
        }

        // Create new scanner and add events
        // @ts-ignore
        this._scanner = new Instascan.Scanner({
            video: this.getVideoElement(),
            continuous: false,
            mirror: false
        });

        this._scanner.addListener('active', () => {
            if (this._listener) {
                this._listener.onStreamStart(this._scanner._camera._stream);
            }
        });

        this._scanner.addListener('inactive', () => {
            if (this._listener) {
                this._listener.onStreamEnd(this._scanner._camera._stream);
            }
        });

        this._scanner.start(this._devices[index]).then(() => {
            this._enabled = true;

        }).catch(function(e) {
            Debug.logError("HWCameraModule: cannot start scanner! " + e);
        });
    }

    /**
     * Async! Enable camera, if already enabled, disable old one and asynchronously start new.
     * You should set listener before this to get "onStart" event.
     *
     * @param {number} [index = 0]
     */
    public enable(index: number) {
        // If enabled, disable
        if (this._enabled) {
            this.disable(this, 'enable', index);
            return;
        }

        // Get camera deviceId
        let device = this._devices[index];
        if (!device) {
            Debug.logError("Non-existing camera on index " + index);
            return;
        }

        this._activeDeviceIndex = index;
        Debug.logInfo("Enabling camera[" + index + "]: " + device.name + " / " + device.id);

        // Create scanner
        this._createScanner(index);
    }

    /**
     * Async! Disable camera and if done, optionally call callback function on object with data.
     *
     * @param {object} [cbObject = undefined]
     * @param {string} [cbFunction = undefined]
     * @param {object} [cbData = undefined]
     */
    public disable(cbObject, cbFunction, cbData) {
        if (!this._enabled) {
            Debug.logInfo("Camera is already disabled");
            return;
        }

        if (!this._scanner) {
            Debug.logInfo("Scanner doesn't exist");
            return;
        }

        this._scanner.stop().then(() => {
            this._enabled = false;

            // Callback
            if (cbObject && cbFunction) {
                cbObject[cbFunction](cbData);
            }

        }).catch(function(e) {
            Debug.logError("HWCameraModule: Cannot stop scanner! " + e);
        });
    }

    /**
     * Listener must have functions:
     * onStreamStart(stream)
     * onStreamEnd(stream)
     *
     * @param {object} listener
     */
    public setListener(listener) {
        this._listener = listener;
    }
}

Scene.registerSceneAPIModule(HWCameraModule);
